import { Group, Text } from "@mantine/core"
import { IconTrash } from "@tabler/icons-react"
import {
  ColumnDef,
  createColumnHelper,
  PaginationState,
} from "@tanstack/react-table"
import { ActionIcon } from "@tfg/ui"
import { useMemo } from "react"

import { AdminResponses } from "@tfg/types/endpoints/admin"

import { queryClient } from "~/backend/queryClient"
import {
  findStandardsWithCertificateTypesQueryKey,
  StandardsWithCertificateTypesResponseDto,
} from "~/gen/api"
import { EditableCellWithTextInput } from "~/pages/Admin/Translations/columns"

import { MultiSelectCell } from "./MultiSelectCell"
import { useDeleteStandardMutation, useUpdateStandardMutation } from "./hooks"

export function useColumns(
  certificatesTypesList: AdminResponses.CertificateTypeList[],
  pagination: PaginationState
) {
  const columnHelper =
    createColumnHelper<StandardsWithCertificateTypesResponseDto>()
  const { mutate: deleteStandard } = useDeleteStandardMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: findStandardsWithCertificateTypesQueryKey({
          pagination,
        }),
      })
    },
  })
  const { mutate: updateStandard, isPending: isLoading } =
    useUpdateStandardMutation({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: findStandardsWithCertificateTypesQueryKey({
            pagination,
          }),
        })
      },
    })

  return useMemo<ColumnDef<StandardsWithCertificateTypesResponseDto, any>[]>(
    () => [
      columnHelper.accessor("id", {
        header: "ID",
        enableSorting: false,
        cell: ({ getValue }) => <Text>{getValue()}</Text>,
      }),
      columnHelper.accessor("name", {
        header: "Name",
        enableSorting: false,
        cell: ({ getValue, row }) => (
          <EditableCellWithTextInput
            key={row.original.id}
            defaultValue={getValue()}
            onSave={(newValue) =>
              updateStandard({
                id: row.original.id,
                name: newValue,
              })
            }
          />
        ),
      }),
      columnHelper.accessor("certificateTypes", {
        header: "Certificates",
        enableSorting: false,
        cell: ({ row }) => {
          const list = certificatesTypesList.map((certificateType) => ({
            value: certificateType.id.toString(),
            label: certificateType.key,
          }))
          const certificateTypes = row.original.certificateTypes
          const _values = certificateTypes.map((certificateType) =>
            certificateType.id.toString()
          )
          return (
            <MultiSelectCell
              data={list}
              initialValues={_values}
              isLoading={isLoading}
              key={row.original.id}
              onChange={(value) => {
                updateStandard({
                  ...row.original,
                  certificateTypes: value,
                })
              }}
            />
          )
        },
      }),
      columnHelper.accessor((row) => row.id, {
        header: () => null,
        id: "actions",
        enableSorting: false,
        cell: ({ row }) => (
          <Group wrap="nowrap" gap="xs">
            <ActionIcon
              icon={<IconTrash />}
              onClick={() => deleteStandard(row.original.id)}
            />
          </Group>
        ),
      }),
    ],
    [
      columnHelper,
      deleteStandard,
      updateStandard,
      certificatesTypesList,
      isLoading,
    ]
  )
}
